<script>
  import { format } from 'svelte-i18n';
  import { Button, Modal, ModalBody, ModalFooter } from '@sveltestrap/sveltestrap';

  import { Icon } from '@client/components/icon';
  import { Loader } from '@client/components/loader';
  import { getNotificationsContext } from '@client/components/notifications';

  import { ImageBulkUploadForm } from '@client/pages/manage/assets/modals/shared';

  import { AssetsService } from '@client/services/assets';

  const { addNotification } = getNotificationsContext();

  const notify = (text, type = 'success', autohide = 2500, position = 'top-right') => {
    addNotification({ text, type, autohide, position });
  };

  export let show = false;
  export let showPreviousModal = false;
  export let refetch = false;

  let fileData = {};
  let files = [];
  let inline = false;
  let uploadSuccess = false;
  let isUpdating = false;
  let fileSizeWarning = false;
  let oversizedFiles = [];

  const MAX_FILE_SIZE = 3 * 1024 * 1024; // 3MB in bytes

  const onBack = () => {
    onClose();

    setTimeout(() => (showPreviousModal = true), 200);
  };

  const onClose = () => {
    fileData = {};
    files = [];
    uploadSuccess = false;
    isUpdating = false;
    fileSizeWarning = false;
    oversizedFiles = [];
    show = false;
  };

  const onKeyUp = (event) => {
    if (event.key === 'Escape') {
      onClose();
    }
  };

  const onRemoveFile = (fileIndex) => {
    const filesArray = Array.from(files);

    filesArray.splice(fileIndex, 1);

    files = filesArray;
  };

  const onFileChange = async () => {
    uploadSuccess = false;
    fileSizeWarning = false;
    oversizedFiles = [];

    if (files.length > 0) {
      const filesArray = Array.from(files);
      oversizedFiles = filesArray.filter((file) => file.size > MAX_FILE_SIZE).map((file) => file.name);

      if (oversizedFiles.length > 0) {
        fileSizeWarning = true;
      }
    }
  };

  const onUploadBulkImages = async () => {
    uploadSuccess = false;

    isUpdating = true;

    for (let bulkFile of files) {
      const file = new FormData();

      file.append('file', bulkFile);

      const response = await AssetsService.bulkUploadFiles(file);

      if (response?.status >= 400) {
        isUpdating = false;
        notify($format('label.FAILURE.ASSET_UPLOAD', { values: { asset: fileData.filename } }), 'error');
        return;
      }

      const { hash, filesize, filename, metadata } = response;

      fileData = { ...fileData, hash, filesize, filename, mimetype: metadata.mimetype, metadata };

      if (inline) {
        let image = await AssetsService.getByHash(fileData.filename, 'images');

        image = { ...image, inline: true };

        const inlineUpdate = await AssetsService.updateImage(image);

        if (inlineUpdate?.status >= 400) {
          isUpdating = false;
          notify($format('label.FAILURE.ASSET_UPLOAD', { values: { asset: fileData.filename } }), 'error');
          return;
        }
      }

      notify($format('label.SUCCESS.ASSET_UPLOAD', { values: { asset: fileData.filename } }));
    }

    isUpdating = false;
    show = false;
    uploadSuccess = true;
    refetch = true;
  };

  $: if (!show) {
    onClose();
  }
</script>

<Modal bind:isOpen={show} close={false} size="lg" fullscreen="lg">
  <Loader backdrop show={isUpdating} />
  <ModalBody id="bulk-modal" class="image-bulk-modal" tabindex="-1">
    <div class="modal-actions">
      <button class="back" on:click={onBack}>
        <Icon name="arrow-left" />
        <span>{$format('label.BACK')}</span>
      </button>

      {#if !isUpdating}
        <button class="close" on:click={onClose}>
          <Icon name="x" />
        </button>
      {/if}
    </div>

    <div>
      <h1>{$format('label.BULK_MODAL_BULK_UPLOAD')}</h1>
      <p>{$format('label.DESCRIPTION.BULK_MODAL_BULK_UPLOAD')}</p>
    </div>

    <div class="modal-body">
      <ImageBulkUploadForm
        bind:files
        bind:inline
        bind:uploadSuccess
        maxFileSize={MAX_FILE_SIZE}
        {onFileChange}
        {onRemoveFile}
        inputId="image-bulk"
      />
    </div>
  </ModalBody>
  <ModalFooter>
    <Button color="primary" on:click={onUploadBulkImages} disabled={!files.length}>
      {$format('label.BULK_MODAL_UPLOAD_IMAGES')}
    </Button>
  </ModalFooter>
</Modal>

<svelte:window on:keyup={onKeyUp} />

<style lang="scss" src="./image-bulk-modal.scss"></style>
